// Shared accessibility-widget settings model. Browser-safe, no DOM access at module scope.

export type Align = "off" | "left" | "center" | "right";
export type Contrast3 = "off" | "high" | "invert";
export type Saturation = "off" | "grayscale" | "low" | "high";

export type Settings = {
  fontScale: number;
  spacing: number;
  lineHeight: number;
  align: Align;
  contrast: Contrast3;
  saturation: Saturation;
  links: boolean;
  headings: boolean;
  dyslexia: boolean;
  bigCursor: boolean;
  pauseMotion: boolean;
  readingGuide: boolean;
  hideImages: boolean;
  tooltips: boolean;
  focusOutline: boolean;
  dictionary: boolean;
  readAloud: boolean;
  oversized: boolean;
  position: "left" | "right";
};

export const STORAGE_KEY = "blancopros-a11y-settings";

export const DEFAULTS: Settings = {
  fontScale: 0,
  spacing: 0,
  lineHeight: 0,
  align: "off",
  contrast: "off",
  saturation: "off",
  links: false,
  headings: false,
  dyslexia: false,
  bigCursor: false,
  pauseMotion: false,
  readingGuide: false,
  hideImages: false,
  tooltips: false,
  focusOutline: false,
  dictionary: false,
  readAloud: false,
  oversized: false,
  position: "left",
};

// Must stay fully self-contained (no outer references): it is serialized into an
// inline <head> script so saved settings apply before first paint.
export function applySettings(s: Settings) {
  const root = document.documentElement;
  root.style.setProperty("--a11y-font-scale", String(1 + s.fontScale * 0.125));
  root.style.setProperty("--a11y-line-height", String(1.5 + s.spacing * 0.25));
  root.style.setProperty("--a11y-letter-spacing", s.spacing * 0.04 + "em");
  root.style.setProperty("--a11y-line-height-only", String(1.6 + s.lineHeight * 0.4));
  const c = root.classList;
  c.toggle("a11y-font-scale", s.fontScale > 0);
  c.toggle("a11y-spacing", s.spacing > 0);
  c.toggle("a11y-line-height", s.lineHeight > 0);
  c.toggle("a11y-align-left", s.align === "left");
  c.toggle("a11y-align-center", s.align === "center");
  c.toggle("a11y-align-right", s.align === "right");
  c.toggle("a11y-contrast-high", s.contrast === "high");
  c.toggle("a11y-contrast-invert", s.contrast === "invert");
  c.toggle("a11y-grayscale", s.saturation === "grayscale");
  c.toggle("a11y-desaturate", s.saturation === "low");
  c.toggle("a11y-saturate", s.saturation === "high");
  c.toggle("a11y-links", s.links);
  c.toggle("a11y-headings", s.headings);
  c.toggle("a11y-dyslexia", s.dyslexia);
  c.toggle("a11y-big-cursor", s.bigCursor);
  c.toggle("a11y-pause-motion", s.pauseMotion);
  c.toggle("a11y-hide-images", s.hideImages);
  c.toggle("a11y-tooltips", s.tooltips);
  c.toggle("a11y-focus-outline", s.focusOutline);
}

export function loadSettings(): Settings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return { ...DEFAULTS, ...JSON.parse(raw) } as Settings;
  } catch {
    /* ignore */
  }
  return DEFAULTS;
}

export function saveSettings(s: Settings) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
  } catch {
    /* ignore */
  }
}

/** Inline script for <head>: re-applies saved settings before the page paints. */
export const a11yBootScript =
  "try{var r=localStorage.getItem(" +
  JSON.stringify(STORAGE_KEY) +
  ");if(r){(" +
  applySettings.toString() +
  ")(Object.assign(" +
  JSON.stringify(DEFAULTS) +
  ",JSON.parse(r)))}}catch(e){}";

export type Profile = { label: string; description: string; patch: Partial<Settings> };

export const PROFILES: Profile[] = [
  { label: "Motor Impaired", description: "Bigger cursor, clear focus, no motion", patch: { bigCursor: true, focusOutline: true, pauseMotion: true, fontScale: 1 } },
  { label: "Blind", description: "Read aloud, larger text, highlights", patch: { readAloud: true, focusOutline: true, fontScale: 2, links: true, headings: true } },
  { label: "Color Blind", description: "Stronger color, marked links", patch: { saturation: "high", links: true } },
  { label: "Dyslexia", description: "Readable font, more spacing", patch: { dyslexia: true, spacing: 2, lineHeight: 2, align: "left", readingGuide: true } },
  { label: "Low Vision", description: "Large text, high contrast", patch: { fontScale: 3, contrast: "high", links: true, focusOutline: true } },
  { label: "Cognitive & Learning", description: "Reading guide, tips, dictionary", patch: { readingGuide: true, spacing: 1, headings: true, tooltips: true, dictionary: true } },
  { label: "Seizure Safe", description: "Stops motion, softer color", patch: { pauseMotion: true, saturation: "low" } },
  { label: "ADHD Friendly", description: "Focus guide, less distraction", patch: { readingGuide: true, pauseMotion: true, saturation: "low", focusOutline: true } },
];

export function isProfileActive(s: Settings, p: Profile) {
  return (Object.keys(p.patch) as (keyof Settings)[]).every((k) => s[k] === p.patch[k]);
}

/** Turning a profile off returns only its own settings to their defaults. */
export function toggleProfile(s: Settings, p: Profile): Settings {
  if (!isProfileActive(s, p)) return { ...s, ...p.patch };
  const next = { ...s } as Record<keyof Settings, unknown>;
  (Object.keys(p.patch) as (keyof Settings)[]).forEach((k) => {
    next[k] = DEFAULTS[k];
  });
  return next as Settings;
}

export function countActive(s: Settings) {
  return (Object.keys(DEFAULTS) as (keyof Settings)[]).filter(
    (k) => k !== "position" && k !== "oversized" && s[k] !== DEFAULTS[k],
  ).length;
}
